import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, isNumber } from 'lodash';
import { connect } from 'react-redux';
import Button from 'reactstrap/lib/Button';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { useTimeout } from 'site-modules/shared/hooks/use-timeout';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { Link } from 'site-modules/shared/components/link/link';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ANSWER, ThumbsSurvey } from 'site-modules/shared/components/thumbs-survey/thumbs-survey';
import { EDMUNDS_LOGO_NO_TEXT } from 'client/images/inline-svgs/edmunds-logo';
import {
  BODY_TYPE_PARAMS,
  ENGINE_TYPE,
  ENGINE_TYPE_PARAMS,
  ENGINE_TYPE_RECOMMENDATION,
  SLIDES,
} from './data/engine-type-quiz';

import './engine-type-quiz.scss';

const CREATIVE_ID = 'ready-for-an-ev-quiz';
const LEGEND_ID = 'engine-type-quiz-legend';

const START_SLIDE = { questionIndex: 0, answer: null, recommendation: null, isLoading: false };
const QUIZ_HEIGHT = { true: '500px', false: '360px' };

function EngineTypeQuizUI({ className, vehicleEngineType, vehicleSrpParams, pageName, visitorId, isMobile }) {
  const [slideHistory, setSlideHistory] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(START_SLIDE);
  const contentRef = useRef();
  const recommendationRef = useRef();
  const fieldsetRef = useRef();
  const [setFadeTimeout] = useTimeout();
  const [setLoadingTimeout] = useTimeout();

  const dotsCount = slideHistory.length + 3;

  function onScrollFade({ target }) {
    if (!target) {
      return;
    }

    target.classList.toggle('fade-bottom', !target.scrollTop && target.scrollHeight > target.clientHeight);
  }

  useEffect(() => {
    onScrollFade({ target: recommendationRef.current });
    onScrollFade({ target: fieldsetRef.current });
  }, [currentSlide]);

  function switchSlide(nextSlide, noHistory) {
    contentRef.current.classList.toggle('fade-out', true);

    setFadeTimeout(() => {
      if (!noHistory) {
        setSlideHistory([...slideHistory, currentSlide]);
      }
      setCurrentSlide(nextSlide);
      contentRef.current.classList.toggle('fade-out', false);
      contentRef.current.focus();
    }, 200);

    if (nextSlide.isLoading) {
      setLoadingTimeout(() => {
        switchSlide({ ...nextSlide, isLoading: false }, true);
      }, 3000);
    }
  }

  function onAnswerClick({ currentTarget: { innerHTML: answer } }) {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_name: TrackingConstant.ACTION_VIEW_CONTENT,
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        subaction_name: SLIDES[currentSlide.questionIndex].subactionName,
        creative_id: CREATIVE_ID,
        value: answer,
      },
    });

    const { nextSlideIndex, recommend } = SLIDES[currentSlide.questionIndex].answers.find(
      ({ value }) => value === answer
    );

    if (recommend) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_name: TrackingConstant.ACTION_VIEW_CONTENT,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          subaction_name: 'quiz_result',
          creative_id: CREATIVE_ID,
          value: recommend.replace('<br/>', ' '),
        },
      });
    }

    switchSlide(recommend ? { recommendation: recommend, isLoading: true } : { questionIndex: nextSlideIndex });
  }

  function onResetClick() {
    setCurrentSlide(START_SLIDE);
    setSlideHistory([]);
  }

  const srpLink = srpLinkBuilder({
    ...(vehicleEngineType === ENGINE_TYPE_PARAMS[currentSlide.recommendation] ? vehicleSrpParams : {}),
    engineType: ENGINE_TYPE_PARAMS[currentSlide.recommendation],
    bodyType: BODY_TYPE_PARAMS[currentSlide.recommendation],
    price: '20000-*',
    sort: 'price:asc',
  });

  const treehouseLink = `https://treehouse.pro/edmunds?utm_campaign=edmunds_clickout_01&utm_content=get_free_quote&utm_source=edmunds_${pageName}&utm_targetid=${visitorId}`;

  return (
    <div
      className={classnames('engine-type-quiz rounded-12 px-1 pt-1_5 pb-1 pos-r', className)}
      data-tracking-parent={CREATIVE_ID}
    >
      <div className="quiz-badge medium text-white bg-blue-50  rounded-8 pos-a top-0 left-0 ms-1 px-0_5 py-0_25">
        <i className="icon-sparkles text-yellow-60" aria-hidden /> Try this quiz!
      </div>
      <h2 className={classnames('heading-3 mb-1 with-loading', { 'fade-out': currentSlide.isLoading })}>
        {currentSlide.recommendation ? (
          <Fragment>
            {currentSlide.recommendation === ENGINE_TYPE.EV_PLUG_IN_HYBRID ? 'Your matches!' : 'Your match!'}
          </Fragment>
        ) : (
          'Is an EV right for me?'
        )}
      </h2>
      <div
        ref={contentRef}
        className="content-pane d-flex flex-column"
        style={{ height: QUIZ_HEIGHT[isMobile] }}
        tabIndex={-1}
        aria-live="polite"
      >
        {isNumber(currentSlide.questionIndex) && (
          <div className="dots d-flex align-items-center justify-content-center mb-0_75">
            {new Array(dotsCount).fill().map((_, index) => {
              const key = `dot-${index}`;
              const isActive = index === dotsCount - 3;

              return (
                <i
                  key={key}
                  className={classnames('dot mx-0_25', {
                    'bg-turquoise-30': isActive,
                    'bg-white inactive': !isActive,
                  })}
                  style={{ borderRadius: '50%', width: '9px', height: '9px' }}
                />
              );
            })}
          </div>
        )}
        {!!currentSlide.isLoading && (
          <Fragment>
            <div className="bg-cool-gray-70 h-100 rounded-12 py-1 px-1 mb-1 overflow-y-hidden">
              <div className="h-100 rounded-8 bg-turquoise-100">
                <div className="d-flex flex-column justify-content-center align-items-center h-100 w-100">
                  <div className="heading-3 mb-1 text-center">
                    Revving up
                    <br />
                    your matches!
                  </div>
                  <div className="edmunds-loader bg-white px-0_5 py-0_5 pos-r">
                    <div className="logo-container h-100 w-100">
                      {EDMUNDS_LOGO_NO_TEXT({ width: '50px', fillColor: 'white', classes: 'pos-a center-xy' })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bottom-placeholder" aria-hidden />
          </Fragment>
        )}
        {!!currentSlide.recommendation && !currentSlide.isLoading && (
          <Fragment>
            <div className="bg-cool-gray-70 h-100 rounded-12 py-1 px-1 mb-1 overflow-y-hidden">
              <div className="h-100 rounded-8 bg-white">
                <div
                  ref={recommendationRef}
                  onScroll={onScrollFade}
                  className="recommendation-text h-100 py-0_75 ps-1 pe-0_75"
                >
                  <div className="d-flex justify-content-between">
                    <ContentFragment classes="heading-4">{currentSlide.recommendation}</ContentFragment>
                    <div className="recommended-icons d-flex flex-nowrap">
                      {ENGINE_TYPE_RECOMMENDATION[currentSlide.recommendation].badges.map(icons => (
                        <div key={`badge-${icons[0]}`} className="recommendation-badge pos-r ms-0_5">
                          {icons.map(icon => (
                            <i
                              key={icon}
                              className={classnames(icon, 'icon pos-a center-xy text-turquoise-20')}
                              aria-hidden
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                  {ENGINE_TYPE_RECOMMENDATION[currentSlide.recommendation].paragraphs.map((paragraph, index) => {
                    const key = `${currentSlide.recommendation}-${index}`;
                    return (
                      <p key={key} className="size-16 mt-1 mb-0">
                        {paragraph}
                      </p>
                    );
                  })}
                  <Button
                    tag={Link}
                    to={srpLink}
                    color="link"
                    size="lg"
                    className="text-blue-50 text-transform-none fw-normal text-underline d-flex align-items-center px-0 py-0 mt-1_5 mb-0_5"
                    data-tracking-id="shop_my_matches"
                    data-tracking-value={srpLink}
                  >
                    Shop my matches
                    <i className="icon-arrow-right3 xsmall ms-0_25" aria-hidden />
                  </Button>
                  <Link
                    to={treehouseLink}
                    target="_blank"
                    className="text-blue-50 text-underline rounded mb-1"
                    data-tracking-id="treehouse_clickout"
                  >
                    Need to install a charger at home? Get a free quote
                  </Link>
                </div>
              </div>
            </div>
            <Row>
              <Col xs={12} md={6} lg={8} xl={6}>
                <ThumbsSurvey
                  question="Was this information helpful?"
                  creativeId={`${CREATIVE_ID}-survey`}
                  className="d-flex align-items-center justify-content-between justify-content-md-start mb-0_5"
                  questionClassName="me-md-1"
                  answerWrapperClassName="d-flex"
                  answerButtonClassName="me-1"
                  textQuestionClassName="d-none"
                  textAreaClassName="py-0_5 ps-0_5 pe-3_5"
                  textAriaPlaceholder="Please tell us why"
                  textSubmitClassName="pos-a right-0 center-y btn btn-blue-50 btn-lg px-0_25 py-0_25 me-0_5 d-flex align-items-center"
                  textSubmitCta={
                    <Fragment>
                      <span className="visually-hidden">Submit your answer</span>
                      <i className="icon-arrow-right8" aria-hidden />
                    </Fragment>
                  }
                  thankYouClassName=""
                  textAreaRowCount={1}
                  trackingMap={{
                    submit: 'submit_ev_quiz_survey',
                    [ANSWER.YES]: 'ev_quiz_survey_yes_comments',
                    [ANSWER.NO]: 'ev_quiz_survey_no_comments',
                  }}
                />
              </Col>
            </Row>
          </Fragment>
        )}
        {isNumber(currentSlide.questionIndex) && (
          <Fragment>
            <div className="question-group mb-0 form-group d-flex flex-column overflow-hidden">
              <div id={LEGEND_ID} className="legend size-16 fw-medium mb-1">
                {SLIDES[currentSlide.questionIndex].question}
              </div>
              <ul
                ref={fieldsetRef}
                aria-labelledby={LEGEND_ID}
                className={classnames('question-list list-unstyled d-flex flex-wrap h-100', { row: !isMobile })}
                onScroll={onScrollFade}
              >
                {SLIDES[currentSlide.questionIndex].answers.map(listItem => (
                  <li
                    key={listItem.value}
                    className={classnames('d-inline-block pb-0_5', {
                      'col-6': !isMobile && currentSlide.questionIndex,
                      'col col-auto': !isMobile && !currentSlide.questionIndex,
                      'me-0_5': isMobile,
                    })}
                  >
                    <button
                      className="answer d-flex w-100 h-100 size-16 bg-white py-0_5 px-1 rounded-12 border-0 text-start"
                      onClick={onAnswerClick}
                    >
                      {listItem.value}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </Fragment>
        )}
        {!currentSlide.isLoading && (!!currentSlide.questionIndex || currentSlide.recommendation) && (
          <div
            className={classnames('d-flex', {
              'flex-column flex-md-row align-items-center justify-content-between': !currentSlide.recommendation,
            })}
          >
            <Button
              onClick={onResetClick}
              color="link"
              className="reset-btn text-blue-40 fw-medium medium text-transform-none text-underline p-0 rounded"
            >
              Start over
              <i className="icon-reset ms-0_25 small" aria-hidden />
            </Button>
            {!currentSlide.recommendation && (
              <Link
                to={treehouseLink}
                target="_blank"
                className="text-blue-40 text-underline text-center rounded mt-1 mt-md-0"
                data-tracking-id="treehouse_clickout"
              >
                Need to install a charger at home? {isMobile && <br />}Get a free quote
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

EngineTypeQuizUI.propTypes = {
  className: PropTypes.string,
  vehicleEngineType: PropTypes.string,
  vehicleSrpParams: PropTypes.shape({}),
  pageName: PropTypes.string,
  visitorId: PropTypes.string,
  isMobile: PropTypes.bool,
};

EngineTypeQuizUI.defaultProps = {
  className: null,
  vehicleEngineType: null,
  vehicleSrpParams: null,
  pageName: null,
  visitorId: null,
  isMobile: false,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  visitorId: get(state, 'visitor.id'),
  pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const EngineTypeQuiz = connect(mapStateToProps)(EngineTypeQuizUI);
